'use client';

import React, { useState, useEffect } from 'react';

const WHATSAPP_LINK = 'https://wa.link/minli1';
const TELEGRAM_LINK = 'https://t.me/AreaFx001';

export default function LandingPage() {
  const [showPopup, setShowPopup] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleWhatsApp = () => {
    window.open(WHATSAPP_LINK, '_blank');
  };

  const handleTelegram = () => {
    window.open(TELEGRAM_LINK, '_blank');
  };

  return (
    <main className="min-h-screen bg-[#0A0E1A] text-[#E8EAF0] overflow-hidden relative">
      {/* Animated background orbs */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute w-[600px] h-[600px] rounded-full opacity-10 blur-[120px]"
          style={{
            background: 'radial-gradient(circle, #00D4AA 0%, transparent 70%)',
            left: mousePos.x * 0.03 - 100,
            top: mousePos.y * 0.03 - 100,
            transition: 'left 1.2s cubic-bezier(0.25,0.46,0.45,0.94), top 1.2s cubic-bezier(0.25,0.46,0.45,0.94)',
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full opacity-8 blur-[100px]"
          style={{
            background: 'radial-gradient(circle, #F43F5E 0%, transparent 70%)',
            right: mousePos.x * 0.02,
            bottom: mousePos.y * 0.02,
            transition: 'right 1.8s cubic-bezier(0.25,0.46,0.45,0.94), bottom 1.8s cubic-bezier(0.25,0.46,0.45,0.94)',
          }}
        />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-5 border-b border-[#1E2A45]/60">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#00D4AA] flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z" stroke="#0A0E1A" strokeWidth="1.5" fill="none"/>
              <circle cx="8" cy="8" r="2" fill="#0A0E1A"/>
            </svg>
          </div>
          <span className="font-bold text-[15px] tracking-tight text-[#E8EAF0]">SignalAI</span>
        </div>
        <button
          onClick={() => setShowPopup(true)}
          className="text-[13px] font-semibold text-[#00D4AA] border border-[#00D4AA]/30 px-4 py-2 rounded-lg hover:bg-[#00D4AA]/10 transition-all duration-200"
        >
          Get Access
        </button>
      </nav>

      {/* Hero */}
      <section className="relative z-10 flex flex-col items-center text-center px-6 pt-24 pb-16">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-[#00D4AA]/10 border border-[#00D4AA]/20 rounded-full px-4 py-1.5 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00D4AA] animate-pulse" />
          <span className="text-[12px] font-semibold text-[#00D4AA] tracking-widest uppercase">Live Signals Active</span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight max-w-4xl mb-6">
          Trade with{' '}
          <span
            className="relative"
            style={{
              background: 'linear-gradient(135deg, #00D4AA 0%, #38BDF8 50%, #818CF8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            AI precision
          </span>
          <br />not guesswork.
        </h1>

        <p className="text-[17px] text-[#A0A8C0] max-w-xl leading-relaxed mb-10">
          Real-time buy &amp; sell signals across stocks, forex, crypto, futures, options, and Deriv Synthetic — powered by multi-indicator AI with entry price, stop-loss, take-profit, and confidence scoring.
        </p>

        <button
          onClick={() => setShowPopup(true)}
          className="group relative inline-flex items-center gap-3 bg-[#00D4AA] text-[#0A0E1A] font-bold text-[16px] px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,212,170,0.4)]"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 0C4.477 0 0 4.477 0 10c0 1.763.46 3.418 1.265 4.855L0 20l5.293-1.238A9.945 9.945 0 0010 20c5.523 0 10-4.477 10-10S15.523 0 10 0zm5.193 14.307c-.22.617-1.29 1.18-1.77 1.254-.48.073-.48.367-3.024-.633-2.543-1-4.13-3.607-4.253-3.773-.123-.167-1.003-1.333-1.003-2.543s.633-1.807.857-2.053c.223-.247.487-.307.65-.307h.467c.15 0 .353-.057.553.423.2.48.68 1.657.74 1.777.06.12.1.26.02.42-.08.16-.12.26-.24.4-.12.14-.253.313-.36.42-.12.12-.247.25-.107.49.14.24.623 1.027 1.337 1.663.917.817 1.69 1.07 1.93 1.19.24.12.38.1.52-.06.14-.16.6-.7.76-.94.16-.24.32-.2.54-.12.22.08 1.4.66 1.64.78.24.12.4.18.46.28.06.1.06.58-.16 1.197z"/>
          </svg>
          Join on WhatsApp
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <button
          onClick={handleTelegram}
          className="group relative inline-flex items-center gap-3 bg-[#229ED9] text-white font-bold text-[16px] px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(34,158,217,0.4)]"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.926 6.8l-1.686 7.946c-.126.558-.456.694-.924.432l-2.56-1.886-1.235 1.188c-.137.137-.252.252-.516.252l.184-2.61 4.752-4.292c.207-.184-.045-.286-.32-.102L5.29 12.394l-2.52-.786c-.548-.172-.558-.548.114-.812l9.856-3.8c.456-.166.856.112.686.804z"/>
          </svg>
          Join on Telegram
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <p className="mt-4 text-[12px] text-[#6B7494]">Free to join · No credit card required</p>
      </section>

      {/* Stats strip */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 mb-20">
        <div className="grid grid-cols-3 gap-4">
          {[
            { value: '94.2%', label: 'Signal Accuracy', color: '#00D4AA' },
            { value: '3,800+', label: 'Active Traders', color: '#38BDF8' },
            { value: '12 Assets', label: 'Covered Daily', color: '#818CF8' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-[#0F1629] border border-[#1E2A45] rounded-xl p-5 text-center hover:border-[#00D4AA]/30 transition-all duration-300"
            >
              <div className="text-2xl font-bold mb-1" style={{ color: stat.color }}>
                {stat.value}
              </div>
              <div className="text-[12px] text-[#6B7494]">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Signal preview cards */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 mb-24">
        <h2 className="text-center text-[13px] font-semibold text-[#6B7494] tracking-widest uppercase mb-8">
          Sample Signals
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { asset: 'BTC/USD', type: 'BUY', entry: '$67,240', tp: '$71,500', sl: '$65,100', confidence: 91, color: '#00D4AA', bg: 'rgba(0,212,170,0.08)', border: 'rgba(0,212,170,0.2)' },
            { asset: 'EUR/USD', type: 'SELL', entry: '1.0842', tp: '1.0780', sl: '1.0890', confidence: 87, color: '#F43F5E', bg: 'rgba(244,63,94,0.08)', border: 'rgba(244,63,94,0.2)' },
            { asset: 'AAPL', type: 'BUY', entry: '$189.40', tp: '$198.00', sl: '$185.20', confidence: 83, color: '#00D4AA', bg: 'rgba(0,212,170,0.08)', border: 'rgba(0,212,170,0.2)' },
            { asset: 'XAU/USD', type: 'SELL', entry: '$2,318', tp: '$2,280', sl: '$2,345', confidence: 79, color: '#F43F5E', bg: 'rgba(244,63,94,0.08)', border: 'rgba(244,63,94,0.2)' },
            { asset: 'Volatility 75', type: 'BUY', entry: '89,450', tp: '92,100', sl: '87,800', confidence: 85, color: '#00D4AA', bg: 'rgba(0,212,170,0.08)', border: 'rgba(0,212,170,0.2)' },
          ].map((signal) => (
            <div
              key={signal.asset}
              className="bg-[#0F1629] border rounded-xl p-5 hover:scale-[1.02] transition-all duration-300 cursor-pointer"
              style={{ borderColor: signal.border, background: signal.bg }}
              onClick={() => setShowPopup(true)}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-bold text-[15px] font-mono">{signal.asset}</span>
                <span
                  className="text-[11px] font-bold tracking-widest px-3 py-1 rounded-md"
                  style={{ color: signal.color, background: signal.bg, border: `1px solid ${signal.border}` }}
                >
                  {signal.type}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { label: 'Entry', value: signal.entry },
                  { label: 'Take Profit', value: signal.tp },
                  { label: 'Stop Loss', value: signal.sl },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="text-[10px] text-[#6B7494] mb-0.5">{item.label}</div>
                    <div className="text-[13px] font-semibold font-mono">{item.value}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="text-[#6B7494]">Confidence</span>
                  <span style={{ color: signal.color }}>{signal.confidence}%</span>
                </div>
                <div className="h-1.5 bg-[#1E2A45] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${signal.confidence}%`, background: signal.color }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-6">
          <button
            onClick={() => setShowPopup(true)}
            className="text-[13px] text-[#00D4AA] hover:underline"
          >
            View all live signals →
          </button>
        </div>
      </section>

      {/* Prop Firm Account Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 mb-24">
        <div className="relative rounded-2xl overflow-hidden border border-[#F59E0B]/30" style={{ background: 'linear-gradient(135deg, #0F1629 0%, #0A1020 60%, #0D1A2E 100%)' }}>
          {/* Gold glow */}
          <div className="absolute inset-0 opacity-15" style={{ background: 'radial-gradient(ellipse at 50% 0%, #F59E0B, transparent 60%)' }} />
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #F59E0B, transparent)' }} />

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 p-8 md:p-10">
            {/* Left: Icon/Badge */}
            <div className="flex-shrink-0 flex flex-col items-center gap-3">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl" style={{ background: 'linear-gradient(135deg, #F59E0B22, #F59E0B44)', border: '1px solid #F59E0B44' }}>
                🏆
              </div>
              <div className="inline-flex items-center gap-1.5 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
                <span className="text-[11px] font-bold text-[#F59E0B] tracking-widest uppercase">Exclusive Bonus</span>
              </div>
            </div>

            {/* Right: Content */}
            <div className="flex-1 text-center md:text-left">
              <p className="text-[#F59E0B] text-[12px] font-bold tracking-widest uppercase mb-2">Included With Every Indicator</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#E8EAF0] mb-3">
                Free <span style={{ color: '#F59E0B' }}>$1,000</span> Instant Funded<br className="hidden md:block" /> Prop Firm Account
              </h2>
              <p className="text-[#A0A8C0] text-[15px] leading-relaxed mb-6">
                Every indicator purchase comes with a complimentary $1,000 instantly funded prop firm account — no evaluation, no waiting. Start trading real capital from day one.
              </p>

              {/* Feature pills */}
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {['No Evaluation Required', 'Instant Funding', '$1,000 Capital', 'Keep 80% Profits', 'Real Account'].map((pill) => (
                  <span key={pill} className="text-[12px] font-medium px-3 py-1 rounded-full border" style={{ background: '#F59E0B11', borderColor: '#F59E0B33', color: '#F59E0B' }}>
                    {pill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 mb-24">
        <h2 className="text-center text-[13px] font-semibold text-[#6B7494] tracking-widest uppercase mb-2">
          Proof
        </h2>
        <p className="text-center text-[#A0A8C0] text-[14px] mb-8">Real results from our WhatsApp signal group</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { src: '/assets/images/IMG-20260527-WA0063-1786404716684.jpg', alt: 'WhatsApp proof screenshot showing trading signal result' },
            { src: '/assets/images/IMG_20260810_223255_990-1786404735953.jpg', alt: 'WhatsApp proof screenshot showing profitable trade outcome' },
            { src: '/assets/images/IMG_20260807_201333_352-1786404751758.jpg', alt: 'WhatsApp proof screenshot showing signal accuracy confirmation' },
          ].map((img, i) => (
            <a
              key={i}
              href="https://wa.link/minli1"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl overflow-hidden border border-[#1E2A45] hover:border-[#00D4AA]/50 hover:scale-[1.02] transition-all duration-300 cursor-pointer"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-auto object-cover"
              />
            </a>
          ))}
        </div>
      </section>

      {/* Copy Trading Section */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 mb-24">
        <div className="relative rounded-2xl overflow-hidden border border-[#1E2A45]" style={{ background: 'linear-gradient(135deg, #0F1629 0%, #0A1020 60%, #0D1A2E 100%)' }}>
          {/* Accent glow */}
          <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(ellipse at 80% 50%, #818CF8, transparent 60%)' }} />
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #818CF8, transparent)' }} />

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 p-8 md:p-12">
            {/* Left: Icon + badge */}
            <div className="flex-shrink-0 flex flex-col items-center md:items-start gap-4">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)', boxShadow: '0 0 40px rgba(129,140,248,0.3)' }}>
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#818CF8]/10 border border-[#818CF8]/20 rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#818CF8] animate-pulse" />
                <span className="text-[11px] font-semibold text-[#818CF8] tracking-widest uppercase">New Feature</span>
              </div>
            </div>

            {/* Right: Content */}
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-[#E8EAF0] mb-3">Copy Trading — Now Live</h2>
              <p className="text-[#A0A8C0] text-[15px] leading-relaxed mb-6">
                Don't have time to watch charts? Let top traders do the work. Automatically mirror expert trades in real time — same entry, same exit, same profit.
              </p>

              {/* Feature pills */}
              <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-8">
                {['Auto-copy trades', 'Real-time execution', 'Verified track record', 'Risk controls'].map((feat) => (
                  <span key={feat} className="text-[12px] text-[#818CF8] bg-[#818CF8]/10 border border-[#818CF8]/20 rounded-full px-3 py-1">{feat}</span>
                ))}
              </div>

              <a
                href="https://copytrader-m5umn61.public.builtwithrocket.new/welcome"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 font-bold text-[15px] px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(129,140,248,0.4)] text-white"
                style={{ background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)' }}
              >
                Start Copy Trading
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 pb-24 text-center">
        <div className="bg-[#0F1629] border border-[#1E2A45] rounded-2xl p-10 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 0%, #00D4AA, transparent 60%)' }} />
          <h2 className="text-3xl font-bold mb-3 relative z-10">Ready to trade smarter?</h2>
          <p className="text-[#A0A8C0] mb-8 relative z-10">Join our WhatsApp or Telegram group and get AI signals delivered directly to your phone.</p>
          <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setShowPopup(true)}
              className="inline-flex items-center gap-3 bg-[#25D366] text-white font-bold text-[15px] px-8 py-4 rounded-xl hover:bg-[#20BD5A] transition-all duration-200 hover:scale-105 hover:shadow-[0_0_30px_rgba(37,211,102,0.4)]"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 0C4.477 0 0 4.477 0 10c0 1.763.46 3.418 1.265 4.855L0 20l5.293-1.238A9.945 9.945 0 0010 20c5.523 0 10-4.477 10-10S15.523 0 10 0zm5.193 14.307c-.22.617-1.29 1.18-1.77 1.254-.48.073-.48.367-3.024-.633-2.543-1-4.13-3.607-4.253-3.773-.123-.167-1.003-1.333-1.003-2.543s.633-1.807.857-2.053c.223-.247.487-.307.65-.307h.467c.15 0 .353-.057.553.423.2.48.68 1.657.74 1.777.06.12.1.26.02.42-.08.16-.12.26-.24.4-.12.14-.253.313-.36.42-.12.12-.247.25-.107.49.14.24.623 1.027 1.337 1.663.917.817 1.69 1.07 1.93 1.19.24.12.38.1.52-.06.14-.16.6-.7.76-.94.16-.24.32-.2.54-.12.22.08 1.4.66 1.64.78.24.12.4.18.46.28.06.1.06.58-.16 1.197z"/>
              </svg>
              Join WhatsApp Group
            </button>
            <button
              onClick={handleTelegram}
              className="inline-flex items-center gap-3 bg-[#229ED9] text-white font-bold text-[15px] px-8 py-4 rounded-xl hover:bg-[#1A8EC4] transition-all duration-200 hover:scale-105 hover:shadow-[0_0_30px_rgba(34,158,217,0.4)]"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.926 6.8l-1.686 7.946c-.126.558-.456.694-.924.432l-2.56-1.886-1.235 1.188c-.137.137-.252.252-.516.252l.184-2.61 4.752-4.292c.207-.184-.045-.286-.32-.102L5.29 12.394l-2.52-.786c-.548-.172-.558-.548.114-.812l9.856-3.8c.456-.166.856.112.686.804z"/>
              </svg>
              Join Telegram Channel
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#1E2A45]/60 px-8 py-6 flex items-center justify-between">
        <span className="text-[12px] text-[#6B7494]">© 2026 SignalAI. All rights reserved.</span>
        <span className="text-[12px] text-[#6B7494]">Trading involves risk. Past performance is not indicative of future results.</span>
      </footer>

      {/* WhatsApp Popup */}
      {showPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(10,14,26,0.85)', backdropFilter: 'blur(12px)' }}
          onClick={(e) => e.target === e.currentTarget && setShowPopup(false)}
        >
          <div
            className="relative w-full max-w-md bg-[#0F1629] border border-[#1E2A45] rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 0 80px rgba(37,211,102,0.15), 0 40px 80px rgba(0,0,0,0.6)' }}
          >
            {/* Green top bar */}
            <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #25D366, #128C7E)' }} />

            {/* Close */}
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-[#1A2140] flex items-center justify-center text-[#6B7494] hover:text-[#E8EAF0] hover:bg-[#1E2A45] transition-all duration-200"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>

            <div className="p-8">
              {/* WhatsApp icon */}
              <div className="flex justify-center mb-6">
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', boxShadow: '0 0 40px rgba(37,211,102,0.3)' }}
                >
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="white">
                    <path d="M20 0C8.954 0 0 8.954 0 20c0 3.526.92 6.836 2.53 9.71L0 40l10.586-2.476A19.89 19.89 0 0020 40c11.046 0 20-8.954 20-20S31.046 0 20 0zm10.386 28.614c-.44 1.234-2.58 2.36-3.54 2.508-.96.146-.96.734-6.048-1.266-5.086-2-8.26-7.214-8.506-7.546-.246-.334-2.006-2.666-2.006-5.086s1.266-3.614 1.714-4.106c.446-.494.974-.614 1.3-.614h.934c.3 0 .706-.114 1.106.846.4.96 1.36 3.314 1.48 3.554.12.24.2.52.04.84-.16.32-.24.52-.48.8-.24.28-.506.626-.72.84-.24.24-.494.5-.214.98.28.48 1.246 2.054 2.674 3.326 1.834 1.634 3.38 2.14 3.86 2.38.48.24.76.2 1.04-.12.28-.32 1.2-1.4 1.52-1.88.32-.48.64-.4 1.08-.24.44.16 2.8 1.32 3.28 1.56.48.24.8.36.92.56.12.2.12 1.16-.32 2.394z"/>
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-center mb-2">Join SignalAI on WhatsApp</h2>
              <p className="text-[#A0A8C0] text-center text-[14px] leading-relaxed mb-8">
                Get real-time AI buy &amp; sell signals, trade setups with entry, stop-loss &amp; take-profit — delivered straight to your WhatsApp.
              </p>

              {/* Benefits */}
              <div className="space-y-3 mb-8">
                {[
                  'Live signals across stocks, forex & crypto',
                  'AI confidence scores & risk/reward ratios',
                  'Instant alerts — never miss a trade setup',
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#25D366]/20 flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1.5 5l2.5 2.5 4.5-5" stroke="#25D366" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-[13px] text-[#A0A8C0]">{benefit}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center justify-center gap-3 text-white font-bold text-[16px] py-4 rounded-xl transition-all duration-200 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(37,211,102,0.4)]"
                style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
              >
                <svg width="22" height="22" viewBox="0 0 22 22" fill="currentColor">
                  <path d="M11 0C4.925 0 0 4.925 0 11c0 1.94.506 3.76 1.392 5.34L0 22l5.82-1.364A10.94 10.94 0 0011 22c6.075 0 11-4.925 11-11S17.075 0 11 0zm5.712 15.737c-.242.68-1.42 1.298-1.947 1.38-.528.08-.528.404-3.326-.696-2.797-1.1-4.543-3.968-4.678-4.15-.136-.184-1.104-1.467-1.104-2.797s.696-1.988.942-2.26c.246-.272.537-.338.715-.338h.514c.165 0 .388-.063.61.465.22.528.748 1.826.814 1.958.066.132.11.286.022.462-.088.176-.132.286-.264.44-.132.154-.278.344-.396.462-.132.132-.272.275-.118.539.154.264.686 1.13 1.47 1.83 1.01.898 1.859 1.177 2.123 1.309.264.132.418.11.572-.066.154-.176.66-.77.836-1.034.176-.264.352-.22.594-.132.242.088 1.54.726 1.804.858.264.132.44.198.506.308.066.11.066.638-.176 1.32z"/>
                </svg>
                Open WhatsApp Now
              </button>

              <p className="text-center text-[11px] text-[#6B7494] mt-4">
                You'll be redirected to WhatsApp to join the group
              </p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}